<template>
  <v-card class="pa-0 mb-0" tile>
    <Loader v-if="loading"/>
    <v-form v-else ref="form" v-model="valid">
      <v-card-title>
        <h4 class="primary--text">
          {{ $t("fleet.addNew") }}
        </h4>
        <v-spacer />
      </v-card-title>
      <v-card-text>
        <v-divider class="mt-2 mb-4" />
        <v-row class="mb-0">
          <v-col cols="12">
            <v-card outlined class="v-card-outline-primary" elevation="3">
              <v-card-title>
                <h5 class="primary--text">{{ $t("vehicle.details") }}</h5>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-row>
                      <v-col cols="12">
                        <label for="registrationNumber">{{ $t("fleet.registrationNumber") }}</label>
                        <v-text-field
                            v-model.number="form.registrationNumber"
                            id="registrationNumber"
                            outlined
                            required
                            validate-on-blur
                            dense
                            single-line
                            hide-details="auto"
                          :rules="[required]"
                          />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <label for="vehicleSizeType">{{ $t("vehicle.vehicleSizeType") }}</label>
                        <v-select
                          v-model="form.size"
                          ref="vehicleSizeType"
                          id="vehicleSizeType"
                          :items="vehicleSizeTypes"
                          item-text="description"
                          item-value="id"
                          outlined
                          required
                          validate-on-blur
                          dense
                          single-line
                          hide-details="auto"
                          :rules="[required]"
                          @input="blurSelect"
                        ></v-select>
                      </v-col>
                      <v-col cols="12">
                        <label for="vehicleType">{{ $t("vehicle.vehicleType") }}</label>
                        <v-select
                          v-model="form.type"
                          ref="vehicleType"
                          id="vehicleType"
                          :items="vehicleTypes"
                          item-text="description"
                          item-value="id"
                          outlined
                          required
                          validate-on-blur
                          dense
                          single-line
                          hide-details="auto"
                          :rules="[required]"
                          @input="blurSelect"
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-row>
                      <v-col cols="12">
                        <label for="name">{{ $t("fleet.name") }}</label>
                        <v-text-field
                            v-model.number="form.name"
                            id="name"
                            outlined
                            required
                            validate-on-blur
                            dense
                            single-line
                            hide-details="auto"
                          :rules="[required]"
                          />
                      </v-col>
                      <v-col cols="12">
                        <label for="load">{{ $t('vehicle.loadForm') }}</label>
                        <v-text-field
                          v-model.number="form.load"
                          id="load"
                          type="number"
                          outlined
                          required
                          validate-on-blur
                          dense
                          single-line
                          hide-details="auto"
                          :rules="[required, onlyNaturalNumbers, fractionDigitsValidation]"
                        />
                      </v-col>
                      <v-col cols="12">
                        <label for="loadMeters">{{ $t('vehicle.loadMeters') }}</label>
                        <v-text-field
                          v-model.number="form.loadMeters"
                          id="loadMeters"
                          type="number"
                          outlined
                          validate-on-blur
                          dense
                          single-line
                          hide-details="auto"
                          :rules="[onlyNaturalNumbers, fractionDigitsValidation]"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" class="d-flex">
                    <v-btn color="primary" outlined depressed @click="showMoreDetailsClick">{{ showMoreDetailsBtnText }}</v-btn>
                  </v-col>
                  <template v-if="showMoreDetails">
                    <v-col cols="12" md="6">
                      <v-row>
                        <v-col cols="12" md="4">
                          <label for="height">{{ $t('vehicle.height') }}</label>
                          <v-text-field
                            v-model.number="form.height"
                            id="height"
                            type="number"
                            outlined
                            required
                            validate-on-blur
                            dense
                            single-line
                            hide-details="auto"
                            :rules="[onlyNaturalNumbers]"
                            @change="calculateVolumeChange"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <label for="width">{{ $t('vehicle.width') }}</label>
                          <v-text-field
                            v-model.number="form.width"
                            id="width"
                            type="number"
                            outlined
                            required
                            validate-on-blur
                            dense
                            single-line
                            hide-details="auto"
                            :rules="[onlyNaturalNumbers]"
                            @change="calculateVolumeChange"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <label for="length">{{ $t('vehicle.length') }}</label>
                          <v-text-field
                            v-model.number="form.length"
                            id="length"
                            type="number"
                            outlined
                            required
                            validate-on-blur
                            dense
                            single-line
                            hide-details="auto"
                            :rules="[onlyNaturalNumbers]"
                            @change="calculateVolumeChange"
                          />
                        </v-col>
                        <v-col cols="12">
                          <label for="volume">{{ $t('vehicle.volume') }}</label>
                          <v-text-field
                            v-model.number="form.volume"
                            id="volume"
                            type="number"
                            outlined
                            required
                            validate-on-blur
                            dense
                            single-line
                            hide-details="auto"
                            :rules="[onlyNaturalNumbers, fractionDigitsValidation]"
                            :disabled="isCalculatedVolume"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-row>
                        <v-col cols="12">
                          <label for="options">{{ $t('vehicle.options') }}</label>
                          <v-select
                            v-model="form.options"
                            id="options"
                            :items="vehicleOptions"
                            item-text="description"
                            item-value="id"
                            outlined
                            required
                            validate-on-blur
                            dense
                            single-line
                            multiple
                            hide-details="auto"
                            @input="blurSelect"
                          ></v-select>
                        </v-col>
                        <v-col cols="12">
                          <label for="loadForms">{{ $t('vehicle.loadForms') }}</label>
                          <v-select
                            v-model="form.loadForms"
                            id="loadForms"
                            :items="vehicleLoadFormTypes"
                            item-text="description"
                            item-value="id"
                            outlined
                            required
                            validate-on-blur
                            dense
                            single-line
                            multiple
                            hide-details="auto"
                            @input="blurSelect"
                          ></v-select>
                        </v-col>
                      </v-row>
                    </v-col>
                  </template>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col>
            <v-card outlined class="v-card-outline-primary" elevation="3">
              <v-row>
                <v-col cols="12">
                  <v-card-title>
                    <h5 class="primary--text">{{ $t('vehicle.additionalInformation') }}</h5>
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-row>
                          <v-col cols="12">
                            <label for="additionalDescription">{{ $t('vehicle.additionalDescription') }}</label>
                            <v-textarea
                              v-model="form.additionalDescription"
                              id="additionalDescription"
                              outlined
                              required
                              validate-on-blur
                              dense
                              single-line
                              no-resize
                              hide-details
                            ></v-textarea>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row>
                          <v-col cols="12" md="6">
                            <label for="value">{{ $t('vehicle.value') }}</label>
                            <v-text-field
                              v-model.number="form.price"
                              id="value"
                              type="number"
                              outlined
                              required
                              validate-on-blur
                              dense
                              single-line
                              hide-details="auto"
                              :rules="[onlyNaturalNumbers, valueFractionDigitsValidation]"
                            />
                          </v-col>
                          <v-col cols="12" md="6">
                            <label for="currency">{{ $t('vehicle.currency') }}</label>
                            <v-select
                              v-model="form.currency"
                              ref="currency"
                              id="currency"
                              :items="allCurrencies"
                              item-text="description"
                              item-value="id"
                              outlined
                              required
                              validate-on-blur
                              dense
                              single-line
                              hide-details
                              @input="blurSelect"
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
        <v-divider class="mt-2 mb-4" />
        <v-card-actions v-if="!loading">
          <v-row justify="center">
            <v-col>
              <v-btn color="primary" outlined depressed class="px-6 float-right" :disabled="loadingForm"
                @click="$router.go(-1)">
                {{ $t("vehicle.cancel") }}
              </v-btn>
            </v-col>
            <v-col>
              <v-btn color="primary" outlined depressed @click="submit" :loading="loadingForm" :disabled="loadingForm">
                {{ $t("common.save") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card-text>
    </v-form>
  </v-card>
</template>

<script>
import Loader from '@/components/Loader'
import Service from '@/services'
import { catchMarketError } from '@/utils/market'
import { fractionDigitsValidation, roundNumber } from '@/utils/helpers'

export default {
  name: 'AddFleetVehicle',
  components: {
    Loader
  },
  data () {
    return {
      loading: true,
      loadingForm: false,
      valid: true,
      showMoreDetails: false,
      form: {
        registrationNumber: null,
        name: null,
        size: null,
        type: null,
        load: null,
        loadMeters: null,
        height: null,
        width: null,
        length: null,
        volume: null,
        options: [],
        loadForms: [],
        additionalDescription: null,
        price: null,
        currency: this.$store.state.userCommonData.defaultCurrency
      },
      vehicleTypes: [],
      vehicleOptions: [],
      required: (v) => !!v || this.$t('validation.fieldRequired'),
      fractionDigitsValidation: (v) => fractionDigitsValidation(v, 3) || this.$t('validation.fractionDigitsValidation', { digits: 3 }),
      valueFractionDigitsValidation: (v) => fractionDigitsValidation(v, 2) || this.$t('validation.fractionDigitsValidation', { digits: 2 }),
      onlyNaturalNumbers: (v) => v ? v > 0 ? true : this.$t('validation.onlyNaturalNumbers') : true,
      isCalculatedVolume: false
    }
  },
  computed: {
    editMode () {
      return this.$route.name === 'EditFleetVehicle'
    },
    vehicleSizeTypes () {
      return this.$store.getters.enums.vehicleSizeTypes
    },
    vehicleLoadFormTypes () {
      return this.$store.getters.enums.vehicleLoadFormTypes
    },
    allCurrencies () {
      return this.$store.getters.allCurrencies.filter(c => c.isActive)
    },
    showMoreDetailsBtnText () {
      return this.showMoreDetails ? this.$t('vehicle.lessDetails') : this.$t('vehicle.moreDetails')
    },
    isNotEmptyUnloadPlace () {
      return this.form.unloadingPlaces.length > 0 && this.form.unloadingPlaces[0] && this.form.unloadingPlaces[0].value && this.form.unloadingPlaces[0].value.country && this.form.unloadingPlaces[0].value.country.length > 0
    }
  },
  async created () {
    await this.fetchData()
    if (this.editMode) {
      await this.fetchDetails()
    }
    this.loading = false
  },
  methods: {
    async fetchData () {
      try {
        const { data: vehicleTypes } = await Service.get('Dictionary/GetVehicleTypes')
        const language = this.$store.getters.userCommonData.language
        this.vehicleTypes = vehicleTypes.map(type => {
          const item = {
            id: type.id,
            description: type.translatedValues.find(value => value.language === language).name
          }
          return item
        })

        const { data: vehicleOptions } = await Service.get('Dictionary/GetVehicleOptions')

        this.vehicleOptions = vehicleOptions.map(type => {
          const item = {
            id: type.id,
            description: type.translatedValues.find(value => value.language === language).name
          }
          return item
        })
      } catch (e) {
      }
    },
    async fetchDetails () {
      try {
        const { data: orderDetails } = await Service.get('Fleet/GetDetails', { vehicleId: this.$route.params.id })
        this.form = {
          ...orderDetails
        }
      } catch (e) {
        await catchMarketError(e, true, () => {
          this.$router.push({ name: 'AddFleetVehicle' })
        })
      }
    },
    async submit () {
      this.$refs.form.validate()
      if (!this.valid) {
        return
      }
      this.loadingForm = true
      try {
        for (const key in this.form) {
          if (typeof this.form[key] === 'string' && this.form[key].length === 0) {
            this.form[key] = null
          }
        }

        if (this.editMode) {
          await Service.put('Fleet/Update', { ...this.form, vehicleId: this.$route.params.id })
        } else {
          await Service.post('Fleet/Add', { ...this.form })
        }

        await this.$router.push({ name: 'Fleet' })
      } catch (e) {
        await catchMarketError(e, true, () => {
          this.$router.go(0)
        })
      } finally {
        this.loadingForm = false
      }
    },
    blurSelect () {
      this.$refs.vehicleSizeType.blur()
      this.$refs.currency.blur()
      this.$refs.vehicleType.blur()
    },
    showMoreDetailsClick () {
      this.showMoreDetails = !this.showMoreDetails
    },
    calculateVolumeChange () {
      if (this.form.width > 0 && this.form.height > 0 && this.form.length > 0) {
        this.calculateVolume()
      } else {
        this.resetVolume()
      }
    },
    calculateVolume () {
      const result = this.form.width * this.form.height * this.form.length
      this.form.volume = roundNumber(result)
      this.isCalculatedVolume = true
    },
    resetVolume () {
      this.form.volume = null
      this.isCalculatedVolume = false
    }
  }
}
</script>
<style>
.template-select {
  padding: 0px 10px !important;
}
</style>
